import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { OnboardingStep } from '../components/onboarding/OnboardingStep';
import { PlanSelectionStep } from '../components/onboarding/PlanSelectionStep';
import { 
  User, Phone, Briefcase, Award, MapPin, BarChart3, Target, 
  Shield, Home, Wrench, ChevronRight, CheckCircle2, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

// Helper component for premium counting micro-animation
function AnimatedCounter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    let start = displayValue;
    const end = value;
    if (start === end) return;

    const duration = 150; // ms
    const startTime = performance.now();

    function update(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.round(start + (end - start) * progress);
      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }, [value]);

  return <span>{displayValue}{suffix}</span>;
}

const ROLES = [
  { id: 'agent', label: 'Real Estate Agent / Wholesaler', icon: Home, desc: 'Get leads, track properties, and close wholesale deals.' },
  { id: 'property_manager', label: 'Property Manager', icon: Briefcase, desc: 'Manage tenants, handle maintenance, and track rent roll.' },
  { id: 'contractor', label: 'Contractor / Maintenance', icon: Wrench, desc: 'Receive jobs, manage maintenance tickets, and upload invoices.' },
  { id: 'tenant', label: 'Tenant', icon: User, desc: 'View lease, pay monthly rent, and submit maintenance tickets.' },
  { id: 'buyer', label: 'Buyer', icon: Target, desc: 'View properties, sign contracts, and access buyer portal.' },
  { id: 'seller', label: 'Seller', icon: Award, desc: 'View active contracts, contact agent, and track home sale.' },
  { id: 'investor', label: 'Investor', icon: BarChart3, desc: 'Analyze portfolio performance and underwrite properties.' },
  { id: 'other', label: 'Other / Custom Business', icon: Shield, desc: 'Customize WholeScale to fit your operational workflows.' }
];

const BUSINESS_TYPES = [
  { id: 'wholesaling', label: 'Wholesaling' },
  { id: 'co-wholesaling', label: 'Co-Wholesaling' },
  { id: 'virtual', label: 'Virtual Wholesaling' },
  { id: 'fix-flip', label: 'Fix & Flip' },
  { id: 'buy-hold', label: 'Buy & Hold' }
];

const CRM_OPTIONS = [
  { id: 'none', label: 'None / Spreadsheets' },
  { id: 'propstream', label: 'PropStream' },
  { id: 'wise-agent', label: 'Wise Agent' },
  { id: 'rei-blackbook', label: 'REI BlackBook' },
  { id: 'podio', label: 'Podio' },
  { id: 'salesforce', label: 'Salesforce' },
  { id: 'hubspot', label: 'HubSpot' },
  { id: 'zoho', label: 'Zoho' },
];

const GOAL_OPTIONS = [
  { id: 'scale-deals', label: 'Scale monthly deal volume' },
  { id: 'automate-marketing', label: 'Automate marketing workflows' },
  { id: 'build-team', label: 'Build and recruit a team' },
  { id: 'improve-conversion', label: 'Improve lead conversion' },
  { id: 'centralize-ops', label: 'Centralize operations' },
  { id: 'ai-assistants', label: 'Deploy AI virtual assistants' }
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { currentUser, updateProfile } = useStore();
  const [step, setStep] = useState(0);

  // Step 0: User Role
  const [selectedRole, setSelectedRole] = useState('agent');

  // Step 1: User Profile info
  const [name, setName] = useState(currentUser?.name || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [company, setCompany] = useState('');
  const [experienceValue, setExperienceValue] = useState(3);

  // Step 2: Wholesaling Focus
  const [market, setMarket] = useState('');
  const [businessTypes, setBusinessTypes] = useState<string[]>([]);

  // Step 3: Current Operations
  const [crmSearch, setCrmSearch] = useState('');
  const [currentCrm, setCurrentCrm] = useState('none');
  const [dealVolumeValue, setDealVolumeValue] = useState(5);

  // Step 4: System Goals
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);

  // Prefill if currentUser details resolve after mount
  useEffect(() => {
    if (currentUser) {
      if (currentUser.name && !name) setName(currentUser.name);
      if (currentUser.phone && !phone) setPhone(currentUser.phone);
    }
  }, [currentUser]);

  const isPortalUser = selectedRole === 'contractor' || selectedRole === 'tenant' || selectedRole === 'buyer' || selectedRole === 'seller';
  const totalSteps = isPortalUser ? 3 : 6;

  const handleStep0Next = () => {
    setStep(1);
  };

  const handleStep1Next = () => {
    if (!name.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    if (isPortalUser) {
      // Contractors and Tenants go straight to success step
      setStep(2);
    } else {
      setStep(2);
    }
  };

  const handleStep2Next = () => {
    if (!market.trim()) {
      toast.error('Please enter your focus market');
      return;
    }
    if (businessTypes.length === 0) {
      toast.error('Please select at least one business focus');
      return;
    }
    setStep(3);
  };

  const handleStep3Next = () => {
    setStep(4);
  };

  const handleStep4Next = () => {
    if (selectedGoals.length === 0) {
      toast.error('Please select at least one goal');
      return;
    }
    setStep(5);
  };

  const handleOnboardingComplete = async (_plan: string) => {
    const loadingToast = toast.loading('Saving your configurations...');
    try {
      // 1. Update general profile fields in state & Supabase profiles
      await updateProfile({
        name,
        phone,
        onboarding_completed: true,
        role: selectedRole,
        bio: isPortalUser 
          ? `${selectedRole.replace('_', ' ')} account created.` 
          : `Real Estate operator in ${market}. Strategy: ${businessTypes.join(', ')}.`,
        yearsExperience: experienceValue,
        settings: {
          onboarding: {
            role: selectedRole,
            years_experience: experienceValue,
            deal_volume: isPortalUser ? 0 : dealVolumeValue,
            current_crm: isPortalUser ? 'none' : currentCrm,
            focus_market: isPortalUser ? '' : market,
            strategies: isPortalUser ? [] : businessTypes,
            goals: isPortalUser ? [] : selectedGoals,
            completed_at: new Date().toISOString()
          }
        }
      });

      toast.dismiss(loadingToast);
      localStorage.removeItem('wholescale-new-signup');
      toast.success('Onboarding completed successfully!');

      // Redirect user to the correct portal or CRM dashboard
      if (selectedRole === 'contractor') {
        navigate(`/contractor-portal/${currentUser?.id || 'demo-token'}`, { replace: true });
      } else if (selectedRole === 'tenant') {
        navigate(`/tenant-portal/${currentUser?.id || 'demo-token'}`, { replace: true });
      } else if (selectedRole === 'buyer' || selectedRole === 'seller') {
        navigate('/client-portal', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: any) {
      toast.dismiss(loadingToast);
      console.error('[Onboarding] Error finalising onboarding:', err);
      toast.error(`Failed to save onboarding profile: ${err.message || err}`);
    }
  };

  const filteredCrmOptions = CRM_OPTIONS.filter(crm => 
    crm.label.toLowerCase().includes(crmSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[var(--t-bg)] flex items-center justify-center py-6 px-4 relative overflow-hidden select-none">
      {/* Premium Background Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--t-primary)]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--t-success)]/10 rounded-full blur-3xl pointer-events-none" />

      {/* Main Glass Container */}
      <div className="w-full max-w-2xl bg-[var(--t-surface)]/60 backdrop-blur-2xl border border-[var(--t-border)] rounded-3xl p-6 shadow-2xl relative z-10 max-h-[90vh] flex flex-col justify-between overflow-hidden">
        
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-6 max-w-md mx-auto w-full">
          {Array.from({ length: totalSteps }, (_, i) => i).map((num) => (
            <div key={num} className="flex items-center flex-1 last:flex-initial">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${
                step >= num 
                  ? 'bg-[var(--t-primary)] text-white shadow-lg shadow-[var(--t-primary)]/30' 
                  : 'bg-[var(--t-surface)] text-[var(--t-text-muted)] border border-[var(--t-border)]'
              }`}>
                {num + 1}
              </div>
              {num < totalSteps - 1 && (
                <div className={`flex-1 h-0.5 mx-2 transition-all ${
                  step > num ? 'bg-[var(--t-primary)]' : 'bg-[var(--t-border)]'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Transition Area */}
        <div className="flex-1 overflow-y-auto pr-1 min-h-[350px] max-h-[55vh] mb-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.2 }}
            >
              {/* STEP 0: Role Selection */}
              {step === 0 && (
                <OnboardingStep
                  title="I am a..."
                  subtitle="Select your primary operational role to customize your workspace dashboard."
                  onNext={handleStep0Next}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[380px] overflow-y-auto p-1">
                    {ROLES.map(role => {
                      const Icon = role.icon;
                      const active = selectedRole === role.id;
                      return (
                        <button
                          key={role.id}
                          type="button"
                          onClick={() => setSelectedRole(role.id)}
                          className={`p-4 rounded-2xl border text-left flex items-start gap-3 transition-all ${
                            active
                              ? 'border-[var(--t-primary)] bg-[var(--t-primary-dim)]/20 text-white shadow-lg'
                              : 'border-[var(--t-border)] bg-black/20 text-[var(--t-text-muted)] hover:border-[var(--t-border-hover)] hover:text-[var(--t-text)]'
                          }`}
                        >
                          <div className={`p-2.5 rounded-xl shrink-0 ${active ? 'bg-[var(--t-primary)]/20 text-[var(--t-primary)]' : 'bg-zinc-800 text-zinc-500'}`}>
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-xs uppercase tracking-wider">{role.label}</p>
                            <p className="text-[10px] text-zinc-500 leading-normal mt-1">{role.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </OnboardingStep>
              )}

              {/* STEP 1: Profile Details */}
              {step === 1 && (
                <OnboardingStep
                  title="Personal Profile"
                  subtitle="Provide basic contact details to configure your billing profile."
                  onNext={handleStep1Next}
                  onBack={() => setStep(0)}
                >
                  <div className="space-y-4 max-w-md mx-auto">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">
                        Full Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="John Doe"
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--t-border)] bg-black/30 text-[var(--t-text)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">
                        Phone Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1 (555) 000-0000"
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--t-border)] bg-black/30 text-[var(--t-text)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">
                        Company Name (Optional)
                      </label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                        <input
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          placeholder="e.g. Apex Acquisitions"
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--t-border)] bg-black/30 text-[var(--t-text)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
                        />
                      </div>
                    </div>

                    <div className="pt-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-3 block text-center">
                        Years of Experience
                      </label>
                      
                      <div className="flex items-center gap-4 px-2">
                        <button
                          type="button"
                          onClick={() => setExperienceValue(prev => Math.max(0, prev - 1))}
                          className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center font-bold text-base transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="range"
                          min="0"
                          max="30"
                          value={experienceValue}
                          onChange={(e) => setExperienceValue(parseInt(e.target.value))}
                          className="flex-1 accent-purple-650 cursor-pointer h-1 bg-zinc-850 rounded-lg appearance-none"
                        />
                        <button
                          type="button"
                          onClick={() => setExperienceValue(prev => Math.min(30, prev + 1))}
                          className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center font-bold text-base transition-colors"
                        >
                          +
                        </button>
                      </div>

                      <div className="text-center mt-2">
                        <span className="text-2xl font-black text-[var(--t-primary)]">
                          <AnimatedCounter value={experienceValue} />
                        </span>
                        <span className="text-xs text-[var(--t-text-secondary)] ml-1 font-semibold">
                          {experienceValue === 0 ? ' (Just starting)' : experienceValue === 1 ? ' Year' : ' Years'}
                        </span>
                      </div>
                    </div>
                  </div>
                </OnboardingStep>
              )}

              {/* STEP 2 (CRM): Strategy Focus */}
              {step === 2 && !isPortalUser && (
                <OnboardingStep
                  title="Business Strategy"
                  subtitle="Define your focus wholesaling markets and preferred strategies."
                  onNext={handleStep2Next}
                  onBack={() => setStep(1)}
                >
                  <div className="space-y-4 max-w-md mx-auto">
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">
                        Focus operational Market (City, State)
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--t-text-muted)]" />
                        <input
                          type="text"
                          value={market}
                          onChange={(e) => setMarket(e.target.value)}
                          placeholder="e.g. Austin, TX"
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[var(--t-border)] bg-black/30 text-[var(--t-text)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--t-primary)]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-2.5 block">
                        Wholesaling Models
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {BUSINESS_TYPES.map(type => {
                          const active = businessTypes.includes(type.id);
                          return (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => {
                                setBusinessTypes(prev =>
                                  prev.includes(type.id)
                                    ? prev.filter(t => t !== type.id)
                                    : [...prev, type.id]
                                );
                              }}
                              className={`py-2 px-3 rounded-xl border text-[10px] font-black uppercase tracking-widest text-center transition-all ${
                                active
                                  ? 'border-[var(--t-primary)] bg-[var(--t-primary-dim)]/20 text-white'
                                  : 'border-[var(--t-border)] bg-black/10 text-zinc-500 hover:text-white hover:border-zinc-700'
                              }`}
                            >
                              {type.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </OnboardingStep>
              )}

              {/* STEP 2 (PORTAL): Contractor/Tenant/Buyer/Seller Success */}
              {step === 2 && isPortalUser && (
                <div className="text-center py-6 space-y-6 max-w-md mx-auto">
                  <div className="w-16 h-16 bg-purple-650/20 border border-purple-500/30 rounded-3xl flex items-center justify-center mx-auto text-purple-400">
                    <CheckCircle2 size={36} />
                  </div>
                  
                  <div>
                    <h2 className="text-2xl font-black uppercase tracking-tight italic">Onboarding Complete</h2>
                    <p className="text-xs text-zinc-400 mt-2">
                      Your portal account is completely free. Click below to proceed to your dedicated operations portal.
                    </p>
                  </div>

                  <button
                    onClick={() => handleOnboardingComplete('free')}
                    className="w-full py-4 rounded-2xl bg-purple-650 hover:bg-purple-600 text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                  >
                    {selectedRole === 'contractor' ? (
                      <><Wrench size={16} /> Go to my Jobs</>
                    ) : selectedRole === 'tenant' ? (
                      <><Home size={16} /> Go to my Portal</>
                    ) : (
                      <><User size={16} /> Go to My Portal</>
                    )}
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {/* STEP 3 (CRM): Current Operations */}
              {step === 3 && !isPortalUser && (
                <OnboardingStep
                  title="Current Operations"
                  subtitle="Specify the CRM platform you are migrating from and monthly volumes."
                  onNext={handleStep3Next}
                  onBack={() => setStep(2)}
                >
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)]">
                          Migrating from CRM
                        </label>
                        <div className="relative w-40">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                          <input 
                            type="text"
                            value={crmSearch}
                            onChange={(e) => setCrmSearch(e.target.value)}
                            placeholder="Search..."
                            className="w-full pl-8 pr-3 py-1 bg-black/40 border border-white/5 rounded-lg text-xs outline-none text-white focus:border-[var(--t-primary)]"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-[160px] overflow-y-auto p-1">
                        {filteredCrmOptions.map(crm => {
                          const active = currentCrm === crm.id;
                          return (
                            <button
                              key={crm.id}
                              type="button"
                              onClick={() => setCurrentCrm(crm.id)}
                              className={`py-2 px-3 rounded-xl border text-[10px] font-bold uppercase tracking-wider text-center transition-all ${
                                active
                                  ? 'border-[var(--t-primary)] bg-[var(--t-primary-dim)]/20 text-white'
                                  : 'border-[var(--t-border)] bg-black/10 text-zinc-500 hover:text-white hover:border-zinc-700'
                              }`}
                            >
                              {crm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pt-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-[var(--t-text-muted)] mb-3 block text-center">
                        Expected Monthly Deal Volume
                      </label>
                      
                      <div className="flex items-center gap-4 px-2">
                        <button
                          type="button"
                          onClick={() => setDealVolumeValue(prev => Math.max(1, prev - 1))}
                          className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center font-bold text-base transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="range"
                          min="1"
                          max="50"
                          value={dealVolumeValue}
                          onChange={(e) => setDealVolumeValue(parseInt(e.target.value))}
                          className="flex-1 accent-purple-650 cursor-pointer h-1 bg-zinc-850 rounded-lg appearance-none"
                        />
                        <button
                          type="button"
                          onClick={() => setDealVolumeValue(prev => Math.min(50, prev + 1))}
                          className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center font-bold text-base transition-colors"
                        >
                          +
                        </button>
                      </div>

                      <div className="text-center mt-2">
                        <span className="text-2xl font-black text-[var(--t-primary)]">
                          <AnimatedCounter value={dealVolumeValue} />
                        </span>
                        <span className="text-xs text-[var(--t-text-secondary)] ml-1 font-semibold">
                          {dealVolumeValue === 50 ? ' + Deals' : dealVolumeValue === 1 ? ' Deal' : ' Deals'} / month
                        </span>
                      </div>
                    </div>
                  </div>
                </OnboardingStep>
              )}

              {/* STEP 4 (CRM): System Goals */}
              {step === 4 && !isPortalUser && (
                <OnboardingStep
                  title="System Goals"
                  subtitle="Select the primary objectives you wish to automate."
                  onNext={handleStep4Next}
                  onBack={() => setStep(3)}
                >
                  <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                    {GOAL_OPTIONS.map(goal => {
                      const active = selectedGoals.includes(goal.id);
                      return (
                        <button
                          key={goal.id}
                          type="button"
                          onClick={() => {
                            setSelectedGoals(prev =>
                              prev.includes(goal.id)
                                ? prev.filter(g => g !== goal.id)
                                : [...prev, goal.id]
                            );
                          }}
                          className={`w-full px-4 py-3 rounded-xl border text-left flex items-center justify-between transition-all ${
                            active
                              ? 'border-[var(--t-primary)] bg-[var(--t-primary-dim)]/20 text-white'
                              : 'border-[var(--t-border)] bg-black/10 text-zinc-500 hover:text-white hover:border-zinc-700'
                          }`}
                        >
                          <span className="font-bold text-xs uppercase tracking-wider">{goal.label}</span>
                          <Target className={`w-4 h-4 ${active ? 'text-[var(--t-primary)]' : 'text-zinc-600'}`} />
                        </button>
                      );
                    })}
                  </div>
                </OnboardingStep>
              )}

              {/* STEP 5 (CRM): Pricing Plan */}
              {step === 5 && !isPortalUser && (
                <PlanSelectionStep
                  dealVolume={dealVolumeValue === 50 ? '50+' : dealVolumeValue > 20 ? '21-50' : dealVolumeValue > 5 ? '6-20' : '1-5'}
                  onNext={handleOnboardingComplete}
                  onBack={() => setStep(4)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
